import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import {
  acceptInvite,
  getInvitePreview,
  type InvitePreview,
  type LoginResponse,
} from '../services/api';
import { useAuthStore } from '../store/authStore';

const EASE = [0.2, 0, 0, 1] as const;

const formContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const formChild: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
};

type PreviewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: InvitePreview }
  | { kind: 'invalid' };

/**
 * Public signup page reached via `/signup?token=...`. The token comes
 * from the email an operator sent the invitee. The page calls
 * `getInvitePreview(token)` on mount to confirm "you're being invited
 * to <Lender X> as <email>" before asking the recipient to pick a
 * password.
 *
 * On a successful accept we drop the returned JWT + user into the
 * auth store directly (mirroring `useAuthStore.login`'s side effect)
 * and bounce to `/lookup` — the lender_human's natural home. The
 * recipient never sees the login page.
 */
export default function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setToken = useAuthStore((s) => s.setToken);
  const setUser = useAuthStore((s) => s.setUser);

  // Read `?token=...` once per render. `useMemo` keeps the value
  // stable across re-renders so the effect below doesn't loop.
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token')?.trim() ?? '';
  }, [location.search]);

  const [previewState, setPreviewState] = useState<PreviewState>(() =>
    token ? { kind: 'loading' } : { kind: 'invalid' },
  );

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the invite preview once on mount (or when the token in the
  // URL changes — operators sometimes paste the link straight into a
  // dev console).
  useEffect(() => {
    if (!token) {
      setPreviewState({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    setPreviewState({ kind: 'loading' });
    getInvitePreview(token)
      .then((data) => {
        if (!cancelled) setPreviewState({ kind: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setPreviewState({ kind: 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handlePasswordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const native = e.nativeEvent as unknown as {
      getModifierState?: (k: string) => boolean;
    };
    if (typeof native.getModifierState === 'function') {
      setCapsLockOn(native.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (previewState.kind !== 'ready') {
      // Defensive — submit shouldn't be reachable without a preview.
      return;
    }
    if (password.length < 8) {
      setError(t('signup.passwordTooShort'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('signup.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const response: LoginResponse = await acceptInvite({
        token,
        password,
        name: name.trim() ? name.trim() : undefined,
      });
      // Drop the freshly-issued credentials into the auth store —
      // same fields `loginRequest` populates so the rest of the SPA
      // can't tell the difference between "logged in via /login" and
      // "logged in via invite accept".
      setToken(response.access_token);
      setUser(response.user);
      // Match LoginPage: lender_human / operator land on the register
      // inventory, debtors on their case list.
      const home = response.user?.role === 'debtor' ? '/min-sag' : '/entries';
      navigate(home, { replace: true });
    } catch {
      setError(t('signup.expiredOrInvalid'));
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Render branches
  // ------------------------------------------------------------------

  if (previewState.kind === 'loading') {
    return (
      <div className="space-y-12 max-w-[440px]">
        <div className="section-label mb-3">{t('auth.regOfficeLabel')}</div>
        <div className="font-mono text-sm text-[var(--color-ink-soft)]">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (previewState.kind === 'invalid') {
    return (
      <div className="space-y-8 max-w-[440px]">
        <header>
          <div className="section-label mb-3">{t('auth.regOfficeLabel')}</div>
          <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
            {t('signup.invalidInvite')}
          </h1>
        </header>
        <p className="text-sm text-[var(--color-ink-soft)] leading-relaxed">
          {t('signup.expiredOrInvalid')}
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-mono text-[12px] text-[var(--color-primary)] hover:text-[var(--color-primary-strong)]"
        >
          {t('signup.backHome')} →
        </Link>
      </div>
    );
  }

  // previewState.kind === 'ready'
  const preview = previewState.data;

  return (
    <div className="space-y-12 max-w-[440px]">
      <motion.header
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
      >
        <div className="section-label mb-3">{t('auth.regOfficeLabel')}</div>
        <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
          {t('signup.title')}
        </h1>
      </motion.header>

      {/* Confirmation panel: the recipient sees who's inviting them
          and at which email BEFORE they pick a password. Mirrors the
          read-only creditor block on the OpenEntryPage. */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: EASE, delay: 0.05 }}
        className="border border-[var(--color-rule)] bg-[var(--color-paper-deep)] p-4 space-y-2"
      >
        <p className="text-sm text-[var(--color-ink)] leading-relaxed">
          {preview.role === 'operator'
            ? t('signup.ledeOperator', { email: preview.email })
            : t('signup.lede', {
                lender: preview.lender_display_name ?? '',
                email: preview.email,
              })}
        </p>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        className="rule pt-8 space-y-6"
        variants={formContainer}
        initial="hidden"
        animate="visible"
        noValidate
      >
        <motion.div variants={formChild}>
          <label htmlFor="signup-name" className="section-label block mb-2">
            {t('signup.nameLabel')}
          </label>
          <input
            id="signup-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className="w-full"
            disabled={submitting}
          />
        </motion.div>

        <motion.div variants={formChild}>
          <label htmlFor="signup-password" className="section-label block mb-2">
            {t('signup.passwordLabel')}
          </label>
          <div className="relative w-full">
            <input
              id="signup-password"
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              onBlur={() => setCapsLockOn(false)}
              onKeyDown={handlePasswordKeyDown}
              onKeyUp={handlePasswordKeyDown}
              autoComplete="new-password"
              spellCheck={false}
              className="w-full pr-10"
              disabled={submitting}
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              aria-label={
                passwordVisible
                  ? t('auth.hidePassword')
                  : t('auth.showPassword')
              }
              aria-pressed={passwordVisible}
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] transition-colors"
            >
              {passwordVisible ? (
                <EyeOff className="w-4 h-4" strokeWidth={1.75} />
              ) : (
                <Eye className="w-4 h-4" strokeWidth={1.75} />
              )}
            </button>
          </div>
          <AnimatePresence>
            {capsLockOn && (
              <motion.div
                key="caps-lock"
                initial={{ opacity: 0, y: -2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-alert)]"
              >
                <span className="inline-block w-1 h-1 rounded-full bg-[var(--color-alert)]" />
                {t('auth.capsLockOn')}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div variants={formChild}>
          <label
            htmlFor="signup-password-confirm"
            className="section-label block mb-2"
          >
            {t('signup.passwordConfirmLabel')}
          </label>
          <input
            id="signup-password-confirm"
            type={passwordVisible ? 'text' : 'password'}
            value={passwordConfirm}
            onChange={(e) => {
              setPasswordConfirm(e.target.value);
              if (error) setError(null);
            }}
            autoComplete="new-password"
            spellCheck={false}
            className="w-full"
            disabled={submitting}
            minLength={8}
          />
        </motion.div>

        <motion.div variants={formChild} className="flex items-center gap-3">
          <motion.button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            animate={
              submitting ? { opacity: [1, 0.7, 1] } : { opacity: 1 }
            }
            transition={
              submitting
                ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.15 }
            }
          >
            <LogIn className="w-4 h-4" strokeWidth={1.5} />
            {submitting ? t('signup.submitting') : t('signup.submit')}
          </motion.button>
        </motion.div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              role="alert"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -2 }}
              transition={{ duration: 0.22, ease: EASE }}
              className="border border-[var(--color-alert)] bg-[var(--color-alert-soft)] px-4 py-3"
            >
              <span className="font-mono text-xs text-[var(--color-alert)] uppercase tracking-wider">
                {error}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.form>
    </div>
  );
}
